/**
 * Maps a row from a bulk-action data grid into the slim "intent stop" shape
 * the v2 routing utility consumes. The row is recurring (`kind: 'daily'`) iff
 * it carries a `configuredServiceEventId`; otherwise on-request. Hardcoding
 * either kind silently mislabels stops on the verifier (CS-2640).
 *
 * Some dialogs key on `row.id`, others on `row.workOrderId` (move-between-days
 * carries both). Prefer `workOrderId` then fall back to `id`.
 *
 * The output is a *projection* of `DailyStop` / `OnRequestStop` — only the
 * fields the executor actually reads off `intent.stops` (`kind`, `workOrderId`,
 * `configuredServiceEventId`, optional `stopId`). Dialogs sit in JS today; when
 * one migrates to TS it will need to widen `RouteOpIntent.stops` (or assemble a
 * full canonical `Stop` via the typed fixtures) to satisfy the typed intent.
 */

import { asRouteSlotId } from 'types/entities/shared/ids'

import type { CseId, RouteSlotId, StopId, WorkOrderId } from 'types/entities/shared/ids'

/** Loose row shape every bulk-action data grid populates from `getRouteStopsGeojson`. */
export interface BulkActionRow {
  readonly id?: string | null
  readonly workOrderId?: string | null
  readonly configuredServiceEventId?: string | null
  // The v3 wire id — without it the whole operation falls back to the v1 bulk endpoints.
  readonly routeSlotId?: string | null
  // Opts the v1-bulk resequence plan into changed-runs batching.
  readonly isChanged?: boolean
}

// Adds the route and date a bulk-dialog grid row currently lives on.
export interface BulkMoveRow extends BulkActionRow {
  readonly routeId?: string | null
  readonly routeSlotId?: string | null
  readonly date?: string | null
}

interface IntentDailyStop {
  readonly kind: 'daily'
  readonly workOrderId: WorkOrderId
  readonly configuredServiceEventId: CseId
  readonly routeSlotId?: RouteSlotId | null
  readonly isChanged?: boolean
}

interface IntentOnRequestStop {
  readonly kind: 'on-request'
  readonly workOrderId: WorkOrderId
  readonly configuredServiceEventId: null
  readonly routeSlotId?: RouteSlotId | null
  readonly isChanged?: boolean
}

interface IntentDailyStopWithStopId extends IntentDailyStop {
  readonly stopId: StopId
}

interface IntentOnRequestStopWithStopId extends IntentOnRequestStop {
  readonly stopId: StopId
}

export type IntentStop = IntentDailyStop | IntentOnRequestStop
export type IntentStopWithStopId = IntentDailyStopWithStopId | IntentOnRequestStopWithStopId

const pickRowId = (row: BulkActionRow | null | undefined): string | undefined => {
  const wo = row?.workOrderId
  if (wo) return wo
  const id = row?.id
  return id ?? undefined
}

const pickIsChanged = (row: BulkActionRow | null | undefined): boolean | undefined => (row?.isChanged === true ? true : undefined)

const pickRouteSlotId = (row: BulkActionRow | null | undefined): RouteSlotId | null =>
  row?.routeSlotId ? asRouteSlotId(row.routeSlotId) : null

export const toIntentStop = (row: BulkActionRow | null | undefined): IntentStop => {
  const workOrderId = pickRowId(row) as WorkOrderId
  const cse = row?.configuredServiceEventId
  const isChanged = pickIsChanged(row)
  const routeSlotId = pickRouteSlotId(row)
  const base = cse
    ? { kind: 'daily' as const, workOrderId, configuredServiceEventId: cse as CseId, routeSlotId }
    : { kind: 'on-request' as const, workOrderId, configuredServiceEventId: null, routeSlotId }
  return isChanged === undefined ? base : { ...base, isChanged }
}

/** Variant that also writes `stopId` (used by resequence + route-optimization). */
export const toIntentStopWithStopId = (row: BulkActionRow | null | undefined): IntentStopWithStopId => {
  const id = pickRowId(row)
  const workOrderId = id as WorkOrderId
  const stopId = id as StopId
  const cse = row?.configuredServiceEventId
  const isChanged = pickIsChanged(row)
  const routeSlotId = pickRouteSlotId(row)
  const base = cse
    ? { kind: 'daily' as const, workOrderId, stopId, configuredServiceEventId: cse as CseId, routeSlotId }
    : { kind: 'on-request' as const, workOrderId, stopId, configuredServiceEventId: null, routeSlotId }
  return isChanged === undefined ? base : { ...base, isChanged }
}
